import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { decodeJwt, isExpired } from '@/auth/jwt';
import { tokenStore } from '@/auth/tokenStore';

/**
 * Single axios instance with two interceptors:
 *
 *   1. Request interceptor — pulls the LIVE token from tokenStore on every
 *      call and sets `Authorization: Bearer <token>`. Same call also
 *      stamps `X-Tenant-Id` from the active tenant context (Platform
 *      admins can override per-request via config.headers).
 *
 *      This is the explicit fix for the v1 anti-pattern: per-method
 *      `const token = sessionStorage.getItem("token")` lines that
 *      captured the token at module-load time and went stale forever.
 *
 *   2. Response interceptor — on 401 we clear the token and fire a
 *      `auth:expired` window event so the auth context can redirect to
 *      /login without every caller having to handle it.
 */

const baseURL = (import.meta.env['VITE_API_BASE_URL'] as string | undefined)?.replace(/\/$/, '') ?? '';

export const api = axios.create({
  baseURL,
  timeout: 30_000,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

let activeTenantId: string | null = null;

export function setActiveTenantId(tenantId: string | null): void {
  activeTenantId = tenantId;
}

/**
 * Public auth endpoints that must never carry a Bearer header — the
 * server's JWT filter will reject the request before login even runs
 * if a stale (expired) token rides along on /auth/login. Match by
 * suffix so it works whether the URL is "/auth/login", "/api/auth/login",
 * or fully-qualified.
 */
const PUBLIC_AUTH_PATHS = ['/auth/login', '/auth/refresh'];

function isPublicAuthCall(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_AUTH_PATHS.some((p) => url.endsWith(p) || url.includes(`${p}?`));
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // 1. Public auth endpoints must go out cleanly — no Authorization
  //    header even if there's a stale token sitting in localStorage.
  if (isPublicAuthCall(config.url)) {
    return config;
  }

  // 2. If we hold a token but it's already past its exp claim, drop it
  //    NOW. Otherwise the request would 401 just to make us clear it,
  //    and on slow connections that's a visible pause.
  const token = tokenStore.get();
  if (token) {
    const claims = decodeJwt(token);
    if (claims && isExpired(claims)) {
      tokenStore.clear();
      window.dispatchEvent(new CustomEvent('auth:expired'));
      return config;
    }
    if (!config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  if (activeTenantId && !config.headers['X-Tenant-Id']) {
    config.headers['X-Tenant-Id'] = activeTenantId;
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      tokenStore.clear();
      // Decoupled signal — auth context listens, no per-call boilerplate.
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }
    return Promise.reject(error);
  },
);

/** Convenience helper that strips the `data` envelope axios returns. */
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const resp = await api.request<T>(config);
  return resp.data;
}

/** Default SWR fetcher that uses the same axios client. */
export const swrFetcher = <T>(url: string): Promise<T> => request<T>({ url, method: 'GET' });
